/**
 * React Query Hooks for Agents
 *
 * Query hooks for agent configuration management
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { agentApi } from '../api';
import type { AgentListItem, AgentConfigResponse } from '@/types';

export const QUERY_KEYS = {
  agents: ['agents'] as const,
  agentsList: () => [...QUERY_KEYS.agents, 'list'] as const,
  agentConfig: (agentType: string) => [...QUERY_KEYS.agents, agentType] as const,
  agentVersions: (agentType: string) => [...QUERY_KEYS.agents, agentType, 'versions'] as const,
  availableAgents: () => [...QUERY_KEYS.agents, 'available'] as const,
};

/**
 * Get list of all agent configurations
 */
export function useAgentsListQuery(includeInactive = false): UseQueryResult<AgentListItem[], Error> {
  return useQuery({
    queryKey: [...QUERY_KEYS.agentsList(), includeInactive],
    queryFn: async () => {
      const response = await agentApi.list(includeInactive);
      return response.agents.map(agent => ({
        id: agent.agent_type,
        agent_type: agent.agent_type,
        name: agent.configuration.name,
        description: agent.configuration.description,
        is_active: agent.is_active,
        version: agent.version,
        updated_at: agent.updated_at,
      }));
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Get specific agent configuration
 */
export function useAgentConfigQuery(
  agentType: string,
  enabled = true
): UseQueryResult<AgentConfigResponse, Error> {
  return useQuery({
    queryKey: QUERY_KEYS.agentConfig(agentType),
    queryFn: async () => {
      // Backend returns a flat dict with config fields + metadata (is_active, version, etc.)
      // Extract metadata and wrap the rest as AgentConfigResponse
      const { is_active, version, created_at, updated_at, ...configuration } =
        await agentApi.get(agentType);
      return {
        agent_type: agentType,
        version,
        is_active,
        created_at,
        updated_at,
        configuration,
      } as AgentConfigResponse;
    },
    enabled: enabled && !!agentType,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Get agent configuration versions (paginated)
 */
export function useAgentVersionsQuery(
  agentType: string,
  enabled = true,
  page = 1,
  pageSize = 20
) {
  return useQuery({
    queryKey: [...QUERY_KEYS.agentVersions(agentType), page],
    queryFn: () => agentApi.getVersions(agentType, page, pageSize),
    enabled: enabled && !!agentType,
    staleTime: 1000 * 60, // 1 minute
  });
}
